/**
 * Returns whether an element is hidden.
 *
 * @param HTMLElement an HTMLElement
 * @returns whether or not the HTMLElement is hidden
 */
export function isHidden(element) {
    return (element.hasAttribute('hidden') ||
        (element.hasAttribute('aria-hidden') && element.getAttribute('aria-hidden') !== 'false') ||
        // A quick and dirty way to check whether the element is hidden. For a more fine-grained check we could use
        // "window.getComputedStyle" but we don't because of bad performance. If the element has visibility set to "hidden"
        // or "collapse", display set to "none" or opacity set to "0" through CSS we won't be able to catch it here. We
        // accept it due to the huge performance benefits.
        element.style.display === `none` ||
        element.style.opacity === `0` ||
        element.style.visibility === `hidden` ||
        element.style.visibility === `collapse`);
}
/**
 * Returns whether an element is disabled.
 *
 * @param HTMLElement an HTMLElement
 * @returns whether or not the HTMLElement is disabled
 */
export function isDisabled(element) {
    return (element.hasAttribute('disabled') ||
        (element.hasAttribute('aria-disabled') && element.getAttribute('aria-disabled') !== 'false'));
}
/**
 * Determines whether an element is focusable.
 * Read more here: https://stackoverflow.com/questions/1599660/which-html-elements-can-receive-focus/1600194#1600194
 * Or here: https://stackoverflow.com/questions/18261595/how-to-check-if-a-dom-element-is-focusable
 *
 * @param element HTMLElement an HTMLElement
 * @returns whether or not the HTMLElement is focusable
 */
export function isFocusable(element) {
    // Discard elements that are removed from the tab order.
    if (element.getAttribute('tabindex') === '-1' || isHidden(element) || isDisabled(element)) {
        return false;
    }
    return (
    // At this point we know that the element can have focus (eg. won't be -1) if the tabindex attribute exists
    element.hasAttribute('tabindex') ||
        // Anchor tags or area tags with a href set
        ((element instanceof HTMLAnchorElement || element instanceof HTMLAreaElement) && element.hasAttribute('href')) ||
        // Form elements which are not disabled
        element instanceof HTMLButtonElement ||
        element instanceof HTMLInputElement ||
        element instanceof HTMLTextAreaElement ||
        element instanceof HTMLSelectElement ||
        // IFrames
        element instanceof HTMLIFrameElement);
}
//# sourceMappingURL=focusable.js.map